import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import flashSyncCrypto from '../utils/crypto';
import { reconstructBookmarkTree } from '../utils/bookmarkTree';
import {
  buildPasswordImportPreview,
  getVisiblePasswordEntries,
  markPasswordDeleted,
  normalizePasswordVault,
  parseChromePasswordCsv,
  upsertPasswordEntry
} from '../utils/passwordVault';
import './Dashboard.css';

const BookmarkFolder = ({ node }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!node.isFolder) {
    return (
      <div className="bookmark-item">
        <span className="bookmark-icon">🔖</span>
        <a href={node.url} target="_blank" rel="noopener noreferrer">
          {node.title}
        </a>
      </div>
    );
  }

  return (
    <div className="bookmark-folder">
      <div className="folder-header" onClick={() => setIsExpanded(current => !current)}>
        <span className="folder-icon">{isExpanded ? '📂' : '📁'}</span>
        <span className="folder-title">{node.title}</span>
        <span className="folder-toggle">{isExpanded ? '▼' : '▶'}</span>
      </div>
      {isExpanded && node.children && (
        <div className="folder-children">
          {node.children.map((child, index) => (
            <BookmarkFolder key={child.id || index} node={child} />
          ))}
        </div>
      )}
    </div>
  );
};

const sortNotes = (items) => [...items].sort((left, right) => {
  const leftTimestamp = left.updatedAt || left.createdAt || 0;
  const rightTimestamp = right.updatedAt || right.createdAt || 0;
  return rightTimestamp - leftTimestamp;
});

const deriveNoteTitle = (title, content) => {
  const trimmedTitle = title.trim();
  if (trimmedTitle) {
    return trimmedTitle;
  }

  const firstLine = content
    .split(/\r?\n/)
    .map(line => line.trim())
    .find(Boolean);

  if (!firstLine) {
    return 'Untitled Note';
  }

  return firstLine.length > 60 ? `${firstLine.slice(0, 57)}...` : firstLine;
};

const notePreview = (content) => {
  const normalized = (content || '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'Empty note';
  }

  return normalized.length > 80 ? `${normalized.slice(0, 77)}...` : normalized;
};

const Dashboard = () => {
  const { user, encryptionKey } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('bookmarks');
  const [syncData, setSyncData] = useState({
    passwords: { count: 0, lastSync: null },
    bookmarks: { count: 0, lastSync: null },
    notes: { count: 0, lastSync: null }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [bookmarkTree, setBookmarkTree] = useState([]);
  const [bookmarksLoaded, setBookmarksLoaded] = useState(false);
  const [masterPassword, setMasterPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [decryptionLoading, setDecryptionLoading] = useState(false);
  const [localEncryptionKey, setLocalEncryptionKey] = useState(null);

  const [notes, setNotes] = useState([]);
  const [notesVersion, setNotesVersion] = useState(0);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ title: '', content: '' });

  const [passwords, setPasswords] = useState([]);
  const [passwordsVersion, setPasswordsVersion] = useState(0);
  const [passwordsLoading, setPasswordsLoading] = useState(false);
  const [passwordsLoaded, setPasswordsLoaded] = useState(false);
  const [passwordsSaving, setPasswordsSaving] = useState(false);
  const [showPasswordEditor, setShowPasswordEditor] = useState(false);
  const [activePasswordId, setActivePasswordId] = useState(null);
  const [passwordDraft, setPasswordDraft] = useState({
    title: '',
    url: '',
    username: '',
    password: ''
  });
  const [passwordSearch, setPasswordSearch] = useState('');
  const [showPasswordSecret, setShowPasswordSecret] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [passwordImportPreview, setPasswordImportPreview] = useState(null);

  const activeEncryptionKey = encryptionKey || localEncryptionKey;

  useEffect(() => {
    const restoreCachedKey = async () => {
      const cachedKey = sessionStorage.getItem('flashsync_ek');
      if (!cachedKey || encryptionKey) return;

      try {
        const key = await flashSyncCrypto.importKey(cachedKey);
        setLocalEncryptionKey(key);
        console.log('[Dashboard] Encryption key restored from session cache');
      } catch (err) {
        sessionStorage.removeItem('flashsync_ek');
        console.warn('[Dashboard] Failed to restore cached key:', err);
      }
    };

    restoreCachedKey();
  }, [encryptionKey]);

  const decryptAndDisplayBookmarks = useCallback(async (key) => {
    setDecryptionLoading(true);

    try {
      const response = await api.downloadData('bookmarks');

      if (!response.data.encryptedBlob) {
        setBookmarkTree([]);
        setBookmarksLoaded(true);
        setSyncData(prev => ({
          ...prev,
          bookmarks: { ...prev.bookmarks, count: 0, lastSync: null }
        }));
        return;
      }

      const decryptedBookmarks = await flashSyncCrypto.decrypt(
        key,
        response.data.encryptedBlob,
        response.data.iv
      );

      const treeStructure = reconstructBookmarkTree(decryptedBookmarks);
      setBookmarkTree(treeStructure);
      setBookmarksLoaded(true);
      setSyncData(prev => ({
        ...prev,
        bookmarks: {
          ...prev.bookmarks,
          count: decryptedBookmarks.length,
          lastSync: response.data.lastModified || prev.bookmarks.lastSync
        }
      }));
    } catch (err) {
      console.error('Error decrypting bookmarks:', err);
      setError('Failed to decrypt bookmarks. Please try again.');
    } finally {
      setDecryptionLoading(false);
    }
  }, []);

  const loadNotesForDisplay = useCallback(async (key) => {
    setNotesLoading(true);

    try {
      const response = await api.downloadData('notes');

      if (!response.data.encryptedBlob) {
        setNotes([]);
        setNotesVersion(0);
        setNotesLoaded(true);
        setSyncData(prev => ({
          ...prev,
          notes: { ...prev.notes, count: 0, lastSync: null }
        }));
        return;
      }

      const decryptedNotes = await flashSyncCrypto.decrypt(
        key,
        response.data.encryptedBlob,
        response.data.iv
      );

      const normalizedNotes = sortNotes(Array.isArray(decryptedNotes) ? decryptedNotes : []);
      setNotes(normalizedNotes);
      setNotesVersion(response.data.version || 0);
      setNotesLoaded(true);
      setSyncData(prev => ({
        ...prev,
        notes: {
          ...prev.notes,
          count: normalizedNotes.length,
          lastSync: response.data.lastModified || prev.notes.lastSync
        }
      }));
    } catch (err) {
      console.error('Error decrypting notes:', err);
      setError('Failed to decrypt notes. Please try again.');
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const loadPasswordsForDisplay = useCallback(async (key) => {
    setPasswordsLoading(true);

    try {
      const response = await api.downloadData('passwords');

      if (!response.data.encryptedBlob) {
        setPasswords([]);
        setPasswordsVersion(0);
        setPasswordsLoaded(true);
        setSyncData(prev => ({
          ...prev,
          passwords: { ...prev.passwords, count: 0, lastSync: null }
        }));
        return;
      }

      const decryptedPasswords = await flashSyncCrypto.decrypt(
        key,
        response.data.encryptedBlob,
        response.data.iv
      );

      const normalizedPasswords = normalizePasswordVault(Array.isArray(decryptedPasswords) ? decryptedPasswords : []);
      const visiblePasswords = getVisiblePasswordEntries(normalizedPasswords);
      setPasswords(normalizedPasswords);
      setPasswordsVersion(response.data.version || 0);
      setPasswordsLoaded(true);
      setSyncData(prev => ({
        ...prev,
        passwords: {
          ...prev.passwords,
          count: visiblePasswords.length,
          lastSync: response.data.lastModified || prev.passwords.lastSync
        }
      }));
    } catch (err) {
      console.error('Error decrypting passwords:', err);
      setError('Failed to decrypt passwords. Please try again.');
    } finally {
      setPasswordsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchSyncData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [passwordsResponse, bookmarksResponse, notesResponse] = await Promise.all([
          api.getSyncMetadata('passwords'),
          api.getSyncMetadata('bookmarks'),
          api.getSyncMetadata('notes')
        ]);

        setSyncData({
          passwords: {
            count: 0,
            lastSync: passwordsResponse.data.lastModified || null
          },
          bookmarks: {
            count: 0,
            lastSync: bookmarksResponse.data.lastModified || null
          },
          notes: {
            count: 0,
            lastSync: notesResponse.data.lastModified || null
          }
        });
      } catch (err) {
        console.error('Error fetching sync data:', err);
        setError('Failed to load sync data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSyncData();
  }, []);

  useEffect(() => {
    if (!loading && activeTab === 'bookmarks' && activeEncryptionKey && !decryptionLoading && !bookmarksLoaded) {
      decryptAndDisplayBookmarks(activeEncryptionKey);
    }
  }, [loading, activeTab, activeEncryptionKey, decryptionLoading, bookmarksLoaded, decryptAndDisplayBookmarks]);

  useEffect(() => {
    if (!loading && activeTab === 'notes' && activeEncryptionKey && !notesLoading && !notesLoaded) {
      loadNotesForDisplay(activeEncryptionKey);
    }
  }, [loading, activeTab, activeEncryptionKey, notesLoading, notesLoaded, loadNotesForDisplay]);

  useEffect(() => {
    if (!loading && activeTab === 'passwords' && activeEncryptionKey && !passwordsLoading && !passwordsLoaded) {
      loadPasswordsForDisplay(activeEncryptionKey);
    }
  }, [loading, activeTab, activeEncryptionKey, passwordsLoading, passwordsLoaded, loadPasswordsForDisplay]);

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setDecryptionLoading(true);

    try {
      const saltResponse = await api.getSalt(user.email);
      if (!saltResponse.salt) {
        throw new Error('Server did not return an encryption salt');
      }

      const saltBuffer = flashSyncCrypto.base64ToArrayBuffer(saltResponse.salt);
      const { key } = await flashSyncCrypto.deriveKeyFromPassword(masterPassword, saltBuffer);

      setLocalEncryptionKey(key);
      setMasterPassword('');
      setShowPasswordPrompt(false);

      const exportedKey = await flashSyncCrypto.exportKey(key);
      sessionStorage.setItem('flashsync_ek', exportedKey);

      if (activeTab === 'notes') {
        await loadNotesForDisplay(key);
      } else if (activeTab === 'passwords') {
        await loadPasswordsForDisplay(key);
      } else {
        await decryptAndDisplayBookmarks(key);
      }
    } catch (err) {
      console.error('Error deriving decryption key:', err);
      if (err?.message?.includes('salt')) {
        setError('Failed to retrieve encryption salt from server.');
      } else {
        setError('Invalid master password or failed to decrypt data.');
      }
    } finally {
      setDecryptionLoading(false);
    }
  };

  const openNoteEditor = (note = null) => {
    setActiveNoteId(note?.id || null);
    setNoteDraft({
      title: note?.title || '',
      content: note?.content || ''
    });
    setShowNoteEditor(true);
  };

  const closeNoteEditor = () => {
    setShowNoteEditor(false);
    setActiveNoteId(null);
    setNoteDraft({ title: '', content: '' });
  };

  const saveNotesToServer = async (nextNotes) => {
    if (!activeEncryptionKey) {
      setShowPasswordPrompt(true);
      return;
    }

    setNotesSaving(true);
    setError(null);

    try {
      const encrypted = await flashSyncCrypto.encrypt(activeEncryptionKey, nextNotes);
      const response = await api.uploadData('notes', {
        encryptedBlob: encrypted.encryptedData,
        iv: encrypted.iv,
        version: notesVersion
      });

      const sortedNotes = sortNotes(nextNotes);
      setNotes(sortedNotes);
      setNotesVersion(response.data.version || notesVersion + 1);
      setNotesLoaded(true);
      setSyncData(prev => ({
        ...prev,
        notes: {
          count: sortedNotes.length,
          lastSync: response.data.lastModified || prev.notes.lastSync
        }
      }));
      closeNoteEditor();
    } catch (err) {
      console.error('Error saving notes:', err);
      if (err.response?.status === 409) {
        await loadNotesForDisplay(activeEncryptionKey);
        setError('Notes changed on another device. The latest encrypted notes were loaded; please review and try again.');
      } else {
        setError('Failed to save notes. Please try again.');
      }
    } finally {
      setNotesSaving(false);
    }
  };

  const handleSaveNote = async () => {
    const title = noteDraft.title || '';
    const content = noteDraft.content || '';

    if (!title.trim() && !content.trim()) {
      setError('Please enter a title or note content before saving.');
      return;
    }

    const now = Date.now();
    const derivedTitle = deriveNoteTitle(title, content);
    const currentNotes = [...notes];

    const nextNotes = activeNoteId
      ? currentNotes.map(note => (
          note.id === activeNoteId
            ? { ...note, title: derivedTitle, content, updatedAt: now }
            : note
        ))
      : [
          {
            id: `web_note_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            title: derivedTitle,
            content,
            createdAt: now,
            updatedAt: now
          },
          ...currentNotes
        ];

    await saveNotesToServer(nextNotes);
  };

  const handleDeleteNote = async () => {
    if (!activeNoteId) {
      return;
    }

    const nextNotes = notes.filter(note => note.id !== activeNoteId);
    await saveNotesToServer(nextNotes);
  };

  const openPasswordEditor = (password = null) => {
    setActivePasswordId(password?.id || null);
    setPasswordDraft({
      title: password?.title || '',
      url: password?.url || password?.origin || '',
      username: password?.username || '',
      password: password?.password || ''
    });
    setShowPasswordSecret(false);
    setShowPasswordEditor(true);
  };

  const closePasswordEditor = () => {
    setShowPasswordEditor(false);
    setActivePasswordId(null);
    setPasswordDraft({
      title: '',
      url: '',
      username: '',
      password: ''
    });
    setShowPasswordSecret(false);
  };

  const savePasswordsToServer = async (nextPasswords) => {
    if (!activeEncryptionKey) {
      setShowPasswordPrompt(true);
      return false;
    }

    setPasswordsSaving(true);
    setError(null);

    try {
      const encrypted = await flashSyncCrypto.encrypt(activeEncryptionKey, nextPasswords);
      const response = await api.uploadData('passwords', {
        encryptedBlob: encrypted.encryptedData,
        iv: encrypted.iv,
        version: passwordsVersion
      });

      const normalizedPasswords = normalizePasswordVault(nextPasswords);
      const visiblePasswords = getVisiblePasswordEntries(normalizedPasswords);
      setPasswords(normalizedPasswords);
      setPasswordsVersion(response.data.version || passwordsVersion + 1);
      setPasswordsLoaded(true);
      setPasswordImportPreview(null);
      setSyncData(prev => ({
        ...prev,
        passwords: {
          count: visiblePasswords.length,
          lastSync: response.data.lastModified || prev.passwords.lastSync
        }
      }));
      closePasswordEditor();
      return true;
    } catch (err) {
      console.error('Error saving passwords:', err);
      if (err.response?.status === 409) {
        await loadPasswordsForDisplay(activeEncryptionKey);
        setError('Passwords changed on another device. The latest encrypted vault was loaded; please review and try again.');
      } else {
        setError('Failed to save passwords. Please try again.');
      }
      return false;
    } finally {
      setPasswordsSaving(false);
    }
  };

  const handleSavePassword = async () => {
    if (!passwordDraft.url.trim() || !passwordDraft.password.trim()) {
      setError('Please enter both a website URL and password before saving.');
      return;
    }

    const now = Date.now();
    const existingPassword = passwords.find(password => password.id === activePasswordId);
    const nextPasswords = upsertPasswordEntry(passwords, {
      ...existingPassword,
      id: activePasswordId || undefined,
      title: passwordDraft.title,
      url: passwordDraft.url,
      username: passwordDraft.username,
      password: passwordDraft.password,
      source: existingPassword?.source || 'manual',
      updatedAt: now
    });

    await savePasswordsToServer(nextPasswords);
  };

  const handleDeletePassword = async () => {
    if (!activePasswordId) {
      return;
    }

    const nextPasswords = markPasswordDeleted(passwords, activePasswordId, { now: Date.now() });
    await savePasswordsToServer(nextPasswords);
  };

  const handlePasswordImportSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const fileText = await file.text();
      const parsed = parseChromePasswordCsv(fileText);
      const preview = buildPasswordImportPreview(passwords, parsed.entries);
      setPasswordImportPreview({
        fileName: file.name,
        invalidRows: parsed.invalidRows,
        ...preview
      });
      setError(null);
    } catch (err) {
      console.error('Error importing password CSV:', err);
      setPasswordImportPreview(null);
      setError(err.message || 'Failed to parse password import file.');
    } finally {
      event.target.value = '';
    }
  };

  const handleConfirmPasswordImport = async () => {
    if (!passwordImportPreview) {
      return;
    }

    const saved = await savePasswordsToServer(passwordImportPreview.mergedEntries);
    if (saved) {
      setPasswordImportPreview(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const filteredPasswords = useMemo(() => {
    const query = passwordSearch.trim().toLowerCase();
    return getVisiblePasswordEntries(passwords).filter(password => {
      if (!query) {
        return true;
      }

      const haystack = [
        password.title,
        password.origin,
        password.url,
        password.username
      ].join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }, [passwords, passwordSearch]);

  return (
    <div className="dashboard-page page-shell">
      <div className="container">

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <div className="dashboard-overview">
          <div className="dashboard-card dashboard-metric-card">
            <h3>Passwords</h3>
            <div className="card-content">
              <div className="stat">
                <span className="stat-value">{syncData.passwords.count}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat">
                <span className="stat-label">Last Sync:</span>
                <span className="stat-text">{formatDate(syncData.passwords.lastSync)}</span>
              </div>
            </div>
          </div>

          <div className="dashboard-card dashboard-metric-card">
            <h3>Bookmarks</h3>
            <div className="card-content">
              <div className="stat">
                <span className="stat-value">{syncData.bookmarks.count}</span>
                <span className="stat-label">Total Items</span>
              </div>
              <div className="stat">
                <span className="stat-label">Last Sync:</span>
                <span className="stat-text">{formatDate(syncData.bookmarks.lastSync)}</span>
              </div>
            </div>
          </div>

          <div className="dashboard-card dashboard-metric-card">
            <h3>Notes</h3>
            <div className="card-content">
              <div className="stat">
                <span className="stat-value">{syncData.notes.count}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat">
                <span className="stat-label">Last Sync:</span>
                <span className="stat-text">{formatDate(syncData.notes.lastSync)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="data-section surface-card">
          <div className="tab-navigation">
            <button
              className={`tab-button ${activeTab === 'passwords' ? 'active' : ''}`}
              onClick={() => setActiveTab('passwords')}
            >
              Passwords
            </button>
            <button
              className={`tab-button ${activeTab === 'bookmarks' ? 'active' : ''}`}
              onClick={() => setActiveTab('bookmarks')}
            >
              Bookmarks
            </button>
            <button
              className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              Notes
            </button>
          </div>

          <div className="tab-content">
            {loading ? (
              <div className="loading-state">Loading data...</div>
            ) : (
              <>
                {activeTab === 'passwords' && (
                  <div className="data-table-container">
                    <div className="bookmark-header">
                      <h3>Password Vault</h3>
                      <div className="dashboard-actions">
                        <label className="btn btn-secondary" htmlFor="password-import-input">
                          Import Chrome CSV
                        </label>
                        <input
                          id="password-import-input"
                          type="file"
                          accept=".csv,text/csv"
                          className="visually-hidden"
                          onChange={handlePasswordImportSelected}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={() => openPasswordEditor()}
                          disabled={!activeEncryptionKey || passwordsSaving}
                        >
                          Add Password
                        </button>
                      </div>
                    </div>

                    {passwordImportPreview && (
                      <div className="import-preview-card">
                        <div className="import-preview-header">
                          <div>
                            <strong>{passwordImportPreview.fileName}</strong>
                            <p>
                              Ready to import {passwordImportPreview.importedCount} rows:
                              {' '}
                              {passwordImportPreview.additions} new and {passwordImportPreview.updates} updates.
                            </p>
                          </div>
                          <div className="import-preview-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setPasswordImportPreview(null)}
                              disabled={passwordsSaving}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleConfirmPasswordImport}
                              disabled={passwordsSaving}
                            >
                              {passwordsSaving ? 'Importing...' : 'Import'}
                            </button>
                          </div>
                        </div>
                        {passwordImportPreview.invalidRows.length > 0 && (
                          <p className="import-warning">
                            Skipped {passwordImportPreview.invalidRows.length} invalid rows from the CSV.
                          </p>
                        )}
                      </div>
                    )}

                    {passwordsLoading ? (
                      <div className="loading-state">Decrypting passwords...</div>
                    ) : !activeEncryptionKey ? (
                      <div className="bookmark-actions">
                        <button className="btn btn-primary" onClick={() => setShowPasswordPrompt(true)}>
                          Unlock Vault
                        </button>
                        <p className="help-text">
                          Enter your master password to decrypt, edit, and import your synced passwords in the browser.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="password-search-row">
                          <input
                            className="form-control"
                            type="search"
                            value={passwordSearch}
                            onChange={(event) => setPasswordSearch(event.target.value)}
                            placeholder="Search by site or username"
                          />
                        </div>

                        {filteredPasswords.length > 0 ? (
                          <div className="password-list">
                            {filteredPasswords.map(password => (
                              <button
                                key={password.id}
                                type="button"
                                className="password-card"
                                onClick={() => openPasswordEditor(password)}
                              >
                                <div className="password-card-header">
                                  <strong>{password.title || password.origin || 'Untitled Password'}</strong>
                                  <span>{formatDate(password.updatedAt || password.createdAt)}</span>
                                </div>
                                <div className="card-row">
                                  <span className="card-field card-url">{password.origin || password.url}</span>
                                  <span className="card-field card-username">{password.username || '(No username)'}</span>
                                  <span className="card-field card-secret">
                                    {visibleSecrets[password.id] ? password.password : '••••••••'}
                                  </span>
                                  <button
                                    type="button"
                                    className="card-eye-btn"
                                    title={visibleSecrets[password.id] ? 'Hide password' : 'Show password'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setVisibleSecrets(prev => ({
                                        ...prev,
                                        [password.id]: !prev[password.id]
                                      }));
                                    }}
                                  >
                                    {visibleSecrets[password.id] ? (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    ) : (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                    )}
                                  </button>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="empty-state">
                            <p>{passwordSearch ? 'No passwords match your search.' : 'No passwords have been synced yet.'}</p>
                            <p>Add one manually here, import a Chrome CSV export, or sync from the extension.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'bookmarks' && (
                  <div className="data-table-container">
                    <div className="bookmark-header">
                      <h3>Your Bookmarks</h3>
                    </div>

                    {decryptionLoading && (
                      <div className="loading-state">Decrypting bookmarks...</div>
                    )}

                    {!decryptionLoading && bookmarkTree.length > 0 && (
                      <div className="bookmark-tree-container">
                        {bookmarkTree.map((node, index) => (
                          <BookmarkFolder key={node.id || index} node={node} />
                        ))}
                      </div>
                    )}

                    {!decryptionLoading && bookmarkTree.length === 0 && (
                      <div className="empty-state">
                        <p>No bookmarks found.</p>
                        <p>Use the Chrome extension to sync your bookmarks.</p>
                      </div>
                    )}

                    {!decryptionLoading && !activeEncryptionKey && (
                      <div className="bookmark-actions">
                        <button className="btn btn-primary" onClick={() => setShowPasswordPrompt(true)}>
                          Unlock Vault
                        </button>
                        <p className="help-text">
                          Enter your master password to decrypt your synced bookmark data in the browser.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'notes' && (
                  <div className="data-table-container">
                    <div className="bookmark-header">
                      <h3>Secret Notes</h3>
                      <button
                        className="btn btn-primary"
                        onClick={() => openNoteEditor()}
                        disabled={!activeEncryptionKey || notesSaving}
                      >
                        Add Note
                      </button>
                    </div>

                    {notesLoading ? (
                      <div className="loading-state">Decrypting notes...</div>
                    ) : !activeEncryptionKey ? (
                      <div className="bookmark-actions">
                        <button className="btn btn-primary" onClick={() => setShowPasswordPrompt(true)}>
                          Unlock Vault
                        </button>
                        <p className="help-text">
                          Enter your master password to decrypt and edit your synced secret notes.
                        </p>
                      </div>
                    ) : notes.length > 0 ? (
                      <div className="note-list">
                        {notes.map(note => (
                          <button
                            key={note.id}
                            type="button"
                            className="note-card"
                            onClick={() => openNoteEditor(note)}
                          >
                            <div className="note-card-header">
                              <strong>{note.title}</strong>
                              <span>{formatDate(note.updatedAt || note.createdAt)}</span>
                            </div>
                            <p>{notePreview(note.content)}</p>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>No notes have been synced yet.</p>
                        <p>Create one here or use the Chrome extension and sync it back.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showPasswordPrompt && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Enter Master Password</h3>
            <p>Please enter your master password to unlock your encrypted passwords, bookmarks, and notes.</p>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="master-password">Master Password</label>
                <input
                  type="password"
                  id="master-password"
                  className="form-control"
                  value={masterPassword}
                  onChange={(event) => setMasterPassword(event.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordPrompt(false);
                    setMasterPassword('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={decryptionLoading}
                >
                  {decryptionLoading ? 'Decrypting...' : 'Decrypt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showNoteEditor && (
        <div className="modal-overlay">
          <div className="modal note-editor-modal">
            <h3>{activeNoteId ? 'Edit Note' : 'New Note'}</h3>
            <div className="form-group">
              <label htmlFor="dashboard-note-title">Title</label>
              <input
                id="dashboard-note-title"
                className="form-control"
                type="text"
                value={noteDraft.title}
                onChange={(event) => setNoteDraft(prev => ({ ...prev, title: event.target.value }))}
                placeholder="Optional title"
              />
            </div>
            <div className="form-group">
              <label htmlFor="dashboard-note-content">Secret text</label>
              <textarea
                id="dashboard-note-content"
                className="form-control"
                rows="10"
                value={noteDraft.content}
                onChange={(event) => setNoteDraft(prev => ({ ...prev, content: event.target.value }))}
                placeholder="Write your note here"
              />
            </div>
            <div className="modal-actions note-editor-actions">
              {activeNoteId && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteNote}
                  disabled={notesSaving}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeNoteEditor}
                disabled={notesSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveNote}
                disabled={notesSaving}
              >
                {notesSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordEditor && (
        <div className="modal-overlay">
          <div className="modal password-editor-modal">
            <h3>{activePasswordId ? 'Edit Password' : 'New Password'}</h3>
            <div className="form-group">
              <label htmlFor="dashboard-password-title">Title</label>
              <input
                id="dashboard-password-title"
                className="form-control"
                type="text"
                value={passwordDraft.title}
                onChange={(event) => setPasswordDraft(prev => ({ ...prev, title: event.target.value }))}
                placeholder="Optional title"
              />
            </div>
            <div className="form-group">
              <label htmlFor="dashboard-password-url">Website URL</label>
              <input
                id="dashboard-password-url"
                className="form-control"
                type="url"
                value={passwordDraft.url}
                onChange={(event) => setPasswordDraft(prev => ({ ...prev, url: event.target.value }))}
                placeholder="https://example.com/login"
              />
            </div>
            <div className="form-group">
              <label htmlFor="dashboard-password-username">Username</label>
              <input
                id="dashboard-password-username"
                className="form-control"
                type="text"
                value={passwordDraft.username}
                onChange={(event) => setPasswordDraft(prev => ({ ...prev, username: event.target.value }))}
                placeholder="alice@example.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="dashboard-password-secret">Password</label>
              <div className="password-secret-row">
                <input
                  id="dashboard-password-secret"
                  className="form-control"
                  type={showPasswordSecret ? 'text' : 'password'}
                  value={passwordDraft.password}
                  onChange={(event) => setPasswordDraft(prev => ({ ...prev, password: event.target.value }))}
                  placeholder="Required"
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordSecret(current => !current)}
                >
                  {showPasswordSecret ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="modal-actions note-editor-actions">
              {activePasswordId && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeletePassword}
                  disabled={passwordsSaving}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closePasswordEditor}
                disabled={passwordsSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSavePassword}
                disabled={passwordsSaving}
              >
                {passwordsSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
